import React, { useState } from 'react';
import { Alert, Card, Descriptions, Spin, Table, Tag, Typography } from 'antd';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import cspmService from '@/services/cspm.service';

const { Title, Text, Paragraph } = Typography;

/**
 * Public read-only customer property portal (Phase 3).
 * Tokenized — no login; no stock mutation.
 */
const CustomerPropertyPortalPublicPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [selectedReceiptId, setSelectedReceiptId] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['cspm', 'public-portal', token],
    queryFn: () => cspmService.getPublicPortal(token!),
    enabled: Boolean(token),
    retry: false,
  });

  const { data: passport, isLoading: loadingPassport } = useQuery({
    queryKey: ['cspm', 'public-portal-passport', token, selectedReceiptId],
    queryFn: () => cspmService.getPublicPortalPassport(token!, selectedReceiptId!),
    enabled: Boolean(token && selectedReceiptId),
    retry: false,
  });

  if (isLoading) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div style={{ maxWidth: 640, margin: '48px auto', padding: 24 }}>
        <Title level={3}>Link not found</Title>
        <Paragraph type="secondary">
          This externally owned property portal link is invalid, expired, or revoked.
        </Paragraph>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 960, margin: '32px auto', padding: 24 }}>
      <SpaceLikeTitle customer={data.customer} note={data.note} />
      <Card title="Open receipts" style={{ marginTop: 16 }}>
        <Table
          rowKey="id"
          dataSource={data.open_receipts || []}
          pagination={false}
          onRow={(row) => ({
            onClick: () => setSelectedReceiptId(String(row.id)),
            style: { cursor: 'pointer' },
          })}
          columns={[
            { title: 'EOP #', dataIndex: 'receipt_number', width: 160 },
            { title: 'Status', dataIndex: 'status', width: 100, render: (v) => <Tag>{String(v)}</Tag> },
            {
              title: 'Inspection',
              dataIndex: 'inspection_status',
              width: 120,
              render: (v) => v || '—',
            },
            {
              title: 'Received',
              dataIndex: 'receipt_datetime',
              render: (v) => (v ? String(v).slice(0, 16) : '—'),
            },
          ]}
        />
      </Card>

      <Card title="On-hand (externally owned)" style={{ marginTop: 16 }}>
        <Table
          rowKey={(r) => `${r.item_id}-${r.warehouse_id}`}
          dataSource={data.on_hand || []}
          pagination={false}
          locale={{ emptyText: 'No on-hand externally owned property' }}
          columns={[
            { title: 'Item', dataIndex: 'item_code', width: 140, render: (v, r) => v || r.item_name || '—' },
            { title: 'Name', dataIndex: 'item_name', ellipsis: true },
            { title: 'Warehouse', dataIndex: 'warehouse_name', width: 160, render: (v) => v || '—' },
            { title: 'Qty', dataIndex: 'available_quantity', width: 100 },
            { title: 'UoM', dataIndex: 'uom', width: 80 },
          ]}
        />
      </Card>

      {selectedReceiptId ? (
        <Card
          title="Passport (read-only)"
          style={{ marginTop: 16 }}
          extra={
            <Text type="secondary" style={{ cursor: 'pointer' }} onClick={() => setSelectedReceiptId(null)}>
              Close
            </Text>
          }
        >
          {loadingPassport ? (
            <Spin />
          ) : passport?.receipt ? (
            <>
              <Descriptions size="small" bordered column={1} style={{ marginBottom: 16 }}>
                <Descriptions.Item label="EOP No.">
                  {String((passport.receipt as { receipt_number?: string }).receipt_number || '')}
                </Descriptions.Item>
                <Descriptions.Item label="Status">
                  {String((passport.receipt as { status?: string }).status || '')}
                </Descriptions.Item>
              </Descriptions>
              <Table
                rowKey={(_, i) => String(i)}
                dataSource={
                  ((passport.receipt as { lines?: Array<Record<string, unknown>> }).lines ||
                    []) as Array<Record<string, unknown>>
                }
                pagination={false}
                size="small"
                columns={[
                  { title: '#', dataIndex: 'line_number', width: 48 },
                  { title: 'Description', dataIndex: 'description' },
                  { title: 'Accepted', dataIndex: 'accepted_qty', width: 90 },
                  { title: 'S/N', dataIndex: 'serial_number', width: 110, render: (v) => v || '—' },
                  { title: 'Heat', dataIndex: 'heat_number', width: 100, render: (v) => v || '—' },
                  { title: 'Quality', dataIndex: 'quality_status', width: 110, render: (v) => v || '—' },
                ]}
              />
            </>
          ) : (
            <Alert type="warning" message="Passport unavailable for this receipt" />
          )}
        </Card>
      ) : null}

      <Paragraph type="secondary" style={{ marginTop: 24 }}>
        Return acknowledgement uses a separate link when issued by your supplier.{' '}
        <Link to="/">Home</Link>
      </Paragraph>
    </div>
  );
};

function SpaceLikeTitle(props: {
  customer: { customer_name?: string; customer_code?: string | null };
  note?: string;
}) {
  return (
    <>
      <Title level={2} style={{ marginBottom: 4 }}>
        Externally owned property portal
      </Title>
      <Text type="secondary">
        {props.customer.customer_name}
        {props.customer.customer_code ? ` · ${props.customer.customer_code}` : ''}
      </Text>
      {props.note ? (
        <Alert type="info" showIcon style={{ marginTop: 16 }} message={props.note} />
      ) : null}
    </>
  );
}

export default CustomerPropertyPortalPublicPage;
